import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PlanRepFormPanel from './components/PlanRepFormPanel';
import DictatePanel from './DictatePanel';
import PasteVerbatimPanel from './PasteVerbatimPanel';
import SignaturePad from './components/SignaturePad';

import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });

const NT_LABEL = {
  progress_note:'Progress Note', group_note:'Group Note', case_mgmt_note:'Case Mgmt Note',
  family_note:'Family Note', assessment:'Assessment', treatment_plan:'Treatment Plan',
  treatment_plan_review:'Tx Plan Review', discharge_summary:'Discharge Summary',
};
const STATUS_STYLE = {
  ai_drafted:{ bg:T.infoBg, fg:T.info, text:'AI Draft' },
  in_review:{ bg:T.infoBg, fg:T.info, text:'In Review' },
  pending_compliance_gate:{ bg:T.warningBg, fg:T.warning, text:'Ready to Sign' },
  kicked_back:{ bg:T.blockingBg, fg:T.blocking, text:'Kicked Back' },
  pending_cosign:{ bg:T.warningBg, fg:T.warning, text:'Pending Co-sign' },
  finalized:{ bg:T.passBg, fg:T.pass, text:'Finalized' },
  amended:{ bg:T.infoBg, fg:T.info, text:'Amended' },
  voided:{ bg:T.b100, fg:T.inkSoft, text:'Voided' },
};
export const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg:T.b100, fg:T.inkSoft, text:status };
  return <span style={{ background:s.bg, color:s.fg, borderRadius:99, padding:'2px 10px',
    fontSize:11.5, fontWeight:600, whiteSpace:'nowrap' }}>{s.text}</span>;
};
const SEV_COLOR = { low:'#C9D64B', moderate:'#E8952F', high:'#D33A2F' };

// ── shared logic (mirrors server) ─────────────────────────
function fieldVisible(f, content) {
  if (!f.show_if) return true;
  return String(content[f.show_if.key] || '') === String(f.show_if.equals);
}
// med_disposition (Medications Returned at Discharge): one row per order from the episode, seeded once,
// then owned by the note content. Dispositions drive the DHCS 44(i) destruction queue at sign.
const MED_DISPOSITIONS = ['Returned to client', 'Retained for destruction', 'Transferred to receiving facility', 'Released to family / caregiver', 'Not applicable - none on hand'];
function MedDispositionTable({ val, locked, episodeId, onChange }) {
  const rows = Array.isArray(val) ? val : [];
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (rows.length || !episodeId || locked) return;
    let dead = false; setLoading(true);
    axios.get(`${API}/api/emr/episodes/${episodeId}/med-disposition-rows`).then(r => {
      if (dead) return;
      const seed = (r.data.rows || []).map(o => ({ order_id: o.order_id, medication_name: o.medication_name, strength: o.dosage_form || '', rx_number: o.rx_number || '',
        controlled_schedule: o.controlled_schedule || null, qty_on_hand: '', disposition: '', qty: '', pharmacy: '', note: '' }));
      onChange(seed.length ? seed : [{ order_id: null, medication_name: '', strength: '', rx_number: '', controlled_schedule: null, qty_on_hand: '', disposition: 'Not applicable - none on hand', qty: '', pharmacy: '', note: '' }]);
    }).catch(() => {}).finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeId, locked, rows.length]);
  const set = (i, k, v) => onChange(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const add = () => onChange([...rows, { order_id: null, medication_name: '', strength: '', rx_number: '', controlled_schedule: null, qty_on_hand: '', disposition: '', qty: '', pharmacy: '', note: '' }]);
  const del = (i) => onChange(rows.filter((_, j) => j !== i));
  const cell = { ...input, padding: '5px 7px', fontSize: 12.5 };
  if (loading) return <div style={{ fontSize: 12.5, color: T.inkSoft }}>Loading medications on this episode...</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}>
        <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1 }}>
          <th style={{ padding: 4 }}>Medication</th><th>Rx #</th><th>On hand</th><th>Disposition</th><th>Qty</th><th>Pharmacy</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${T.b100}`, background: r.controlled_schedule ? '#fffbeb' : undefined }}>
              <td style={{ padding: 4, minWidth: 180 }}>
                {r.order_id ? <><strong>{r.medication_name}</strong>{r.strength ? <span style={{ color: T.inkSoft }}> {r.strength}</span> : null}{r.controlled_schedule && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#b45309' }}>C-{r.controlled_schedule}</span>}</>
                  : <input style={cell} disabled={locked} placeholder="Medication (not on an order)" value={r.medication_name} onChange={e => set(i, 'medication_name', e.target.value)} />}
              </td>
              <td><input style={{ ...cell, width: 90 }} disabled={locked} value={r.rx_number || ''} onChange={e => set(i, 'rx_number', e.target.value)} /></td>
              <td><input style={{ ...cell, width: 64 }} disabled={locked} inputMode="numeric" value={r.qty_on_hand || ''} onChange={e => set(i, 'qty_on_hand', e.target.value)} /></td>
              <td><select style={{ ...cell, minWidth: 200 }} disabled={locked} value={r.disposition || ''} onChange={e => set(i, 'disposition', e.target.value)}>
                <option value="">Select...</option>{MED_DISPOSITIONS.map(d => <option key={d} value={d}>{d}</option>)}</select></td>
              <td><input style={{ ...cell, width: 64 }} disabled={locked} inputMode="numeric" value={r.qty || ''} onChange={e => set(i, 'qty', e.target.value)} /></td>
              <td><input style={{ ...cell, width: 120 }} disabled={locked} value={r.pharmacy || ''} onChange={e => set(i, 'pharmacy', e.target.value)} /></td>
              <td>{!locked && <button type="button" onClick={() => del(i)} style={{ border: 'none', background: 'none', color: T.inkSoft, cursor: 'pointer', fontSize: 14 }} title="Remove row">{'\u00d7'}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!locked && <button type="button" onClick={add} style={{ marginTop: 6, background: 'none', border: 'none', color: '#0878E8', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 }}>+ Add a medication not on an order</button>}
      {rows.some(r => r.controlled_schedule) && <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 6, fontWeight: 600 }}>Controlled substance(s) listed - a second staff witness to the count is required below.</div>}
    </div>
  );
}

function fieldAnswered(f, val) {
  if (val === null || val === undefined) return false;
  switch (f.type) {
    case 'narrative': return String(val).trim().length >= (Number(f.min_chars) || 10);
    case 'rating_scale': return val !== '' && !isNaN(Number(val));
    case 'multi_choice': return Array.isArray(val) && val.length > 0;
    case 'single_choice': return String(val).trim().length > 0;
    case 'yesno': return val === 'yes' || val === 'no';
    case 'time_range': return !!(val && val.start);
    case 'time': return String(val).trim().length > 0;
    case 'med_disposition': return Array.isArray(val) && val.length > 0 && val.every(r => r.disposition && String(r.disposition).trim());
    default: return String(val).trim().length > 0;
  }
}
export function mirrorFormRules(formDef, content) {
  const out = [];
  for (const f of (formDef.fields || [])) {
    if (!f.required || !fieldVisible(f, content)) continue;
    out.push({ code:'REQ_' + f.key.toUpperCase(), desc:'Required: ' + f.label,
      severity:'blocking', passed: fieldAnswered(f, content[f.key]) });
  }
  const gt = (formDef.fields || []).find(f => f.golden_thread);
  if (gt) out.push({ code:'GOLDEN_THREAD', desc:'Golden thread: ' + gt.label,
    severity:'warning', passed: fieldAnswered(gt, content[gt.key]) });
  for (const r of (formDef.rules || [])) {
    let trig = false;
    if (r.when && r.when.equals !== undefined) trig = String(content[r.when.key] || '') === String(r.when.equals);
    else if (r.when && Array.isArray(r.when.any_yes)) trig = r.when.any_yes.some(k => content[k] === 'yes');
    if (!trig) { out.push({ code:r.code, desc:r.description, severity:r.severity, passed:true }); continue; }
    if (!r.require || !r.require.key) { out.push({ code:r.code, desc:r.description, severity:r.severity, passed:true }); continue; }
    const v = content[r.require.key];
    out.push({ code:r.code, desc:r.description, severity:r.severity,
      passed: !!(v && String(v).trim().length >= (r.require.min_chars || 1)) });
  }
  return out;
}
// legacy mirror for notes without a form
const LEGACY_REQ = {
  progress_note:['presentation','interventions','response','plan'],
  group_note:['participation','response','plan'],
  case_mgmt_note:['narrative','plan'], family_note:['narrative'], assessment:['narrative'],
  treatment_plan:['narrative'], treatment_plan_review:['narrative'], discharge_summary:['narrative'],
};
function mirrorLegacy(noteType, content) {
  return (LEGACY_REQ[noteType] || []).map(f => ({
    code:'REQ_' + f.toUpperCase(), desc:'Required: ' + f, severity:'blocking',
    passed: !!(content[f] && String(content[f]).trim().length >= 10) }));
}

// ── field renderers ─────────────────────────
function ChoiceChips({ options, value, multi, disabled, onChange }) {
  const sel = multi ? (Array.isArray(value) ? value : []) : value;
  const toggle = (opt) => {
    if (disabled) return;
    if (multi) {
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt]);
    } else onChange(value === opt ? '' : opt);
  };
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
      {options.map(opt => {
        const on = multi ? sel.includes(opt) : sel === opt;
        return (
          <span key={opt} onClick={() => toggle(opt)}
            style={{ cursor: disabled ? 'default' : 'pointer', userSelect:'none',
              background: on ? T.rose : '#fff', color: on ? '#fff' : T.ink,
              border:`1px solid ${on ? T.rose : T.b200}`, borderRadius:99,
              padding:'4px 12px', fontSize:12.5, fontWeight: on ? 700 : 500 }}>{opt}</span>
        );
      })}
    </div>
  );
}
function RatingRow({ min, max, value, disabled, onChange }) {
  const nums = []; for (let i = min; i <= max; i++) nums.push(i);
  return (
    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
      {nums.map(n => {
        const on = String(value) === String(n);
        return (
          <span key={n} onClick={() => !disabled && onChange(on ? '' : String(n))}
            style={{ cursor: disabled ? 'default' : 'pointer', userSelect:'none',
              width:30, height:30, display:'inline-flex', alignItems:'center', justifyContent:'center',
              background: on ? T.rose : '#fff', color: on ? '#fff' : T.ink,
              border:`1px solid ${on ? T.rose : T.b200}`, borderRadius:8,
              fontSize:12.5, fontWeight:600 }}>{n}</span>
        );
      })}
    </div>
  );
}
function YesNo({ value, disabled, sev, onChange }) {
  const yesBg = SEV_COLOR[sev] || T.b200;
  return (
    <div style={{ display:'flex', gap:8 }}>
      {['yes','no'].map(v => {
        const on = value === v;
        return (
          <span key={v} onClick={() => !disabled && onChange(on ? '' : v)}
            style={{ cursor: disabled ? 'default' : 'pointer', userSelect:'none',
              padding:'5px 22px', borderRadius:8, fontSize:12.5, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'.5px',
              background: on ? (v === 'yes' ? yesBg : T.inkSoft) : '#fff',
              color: on ? '#fff' : T.ink,
              border:`1px solid ${on ? 'transparent' : T.b200}` }}>{v}</span>
        );
      })}
    </div>
  );
}
function DateTimeField({ f, val, locked, setField }) {
  React.useEffect(() => {
    if (f.default_now && !val && !locked) {
      const d = new Date();
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      setField(f.key, d.toISOString().slice(0, 16));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input type="datetime-local" style={{ ...input, width: 230 }} disabled={locked}
      value={val || ''} onChange={e => setField(f.key, e.target.value)} />
  );
}

function TimeRange({ value, disabled, onChange }) {
  const v = value || {};
  return (
    <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
      <div><div style={{ ...label, marginBottom:3 }}>Start</div>
        <input type="datetime-local" style={{ ...input, width:210 }} disabled={disabled}
          value={v.start || ''} onChange={e => onChange({ ...v, start:e.target.value })} /></div>
      <div><div style={{ ...label, marginBottom:3 }}>End</div>
        <input type="datetime-local" style={{ ...input, width:210 }} disabled={disabled}
          value={v.end || ''} onChange={e => onChange({ ...v, end:e.target.value })} /></div>
    </div>
  );
}

function AutoSumField({ f, content, setField }) {
  const keys = Array.isArray(f.auto_sum) ? f.auto_sum : [];
  const sum = keys.reduce((t, k) => t + (Number(content[k]) || 0), 0);
  const allScored = keys.every(k => content[k] !== '' && content[k] !== undefined && content[k] !== null);
  useEffect(() => {
    if (allScored && Number(content[f.key]) !== sum) setField(f.key, sum);
  }, [sum, allScored]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ fontSize:20, fontWeight:800, background:T.bg, borderRadius:8, padding:'10px 16px', display:'inline-block' }}>
      {allScored ? sum : `${sum} (items remaining)`}
    </div>
  );
}

function NarrativeWithNow({ f, val, locked, setField }) {
  useEffect(() => {
    if (!val && !locked) {
      const d = new Date();
      setField(f.key, `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea style={{ ...input, minHeight:44, resize:'vertical' }} disabled={locked}
      value={val || ''} onChange={e => setField(f.key, e.target.value)} />
  );
}

export class EditorErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 20, background: '#FDECEC', borderRadius: 10, margin: 16 }}>
        <div style={{ fontWeight: 700, color: '#B3261E', marginBottom: 6 }}>This document failed to render</div>
        <div style={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: '#5F1410' }}>
          {String(this.state.error && (this.state.error.message || this.state.error))}
          {'\n'}{String((this.state.error && this.state.error.stack || '').split('\n').slice(0, 4).join('\n'))}
        </div>
        <button onClick={() => this.setState({ error: null })} style={{ marginTop: 10, padding: '7px 14px',
          borderRadius: 8, border: 'none', cursor: 'pointer', background: '#B3261E', color: '#fff', fontWeight: 600 }}>
          Back
        </button>
      </div>
    );
    return this.props.children;
  }
}

export function resolveTokens(text, facilityName, rates, identity) {
  if (!text || typeof text !== 'string') return text;
  let out = facilityName ? text.split('{{facility_name}}').join(facilityName) : text;
  if (identity) {
    out = out.split('{{facility_address_inline}}').join(identity.address_inline || '')
             .split('{{facility_address}}').join(identity.address || '')
             .split('{{facility_phone}}').join(identity.phone || '')
             .split('{{facility_fax}}').join(identity.fax || '')
             .split('{{facility_privacy_contact}}').join(identity.privacy_contact || '');
  }
  if (rates) {
    const fmt = v => '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });
    for (const [loc, v] of Object.entries(rates))
      out = out.split('{{rate_' + loc.toLowerCase().replace(/ /g, '_') + '}}').join(fmt(v));
  }
  // A facility without a given LOC leaves its {{rate_*}} unresolved: drop the sentence
  // that mentions it rather than show a level of care this facility does not offer.
  out = out.replace(/[^.\n]*\{\{rate_[a-z_]+\}\}[^.]*\./g, '').replace(/  +/g, ' ');
  return out;
}

// Facility-scoped staff picker: types like text, drops down staff at the chart's
// facility, writes "First Last, CRED" as a plain string on pick. Free-typing allowed.
export function StaffTypeahead({ val, locked, onPick, episodeId }) {
  const [opts, setOpts] = useState(null);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    if (!episodeId) return;
    let dead = false;
    axios.get(`${API}/api/emr/staff-directory`, { params: { episode_id: episodeId } })
      .then(r => { if (!dead) setOpts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!dead) setOpts([]); });
    return () => { dead = true; };
  }, [episodeId]);
  const q = String(val || '').toLowerCase();
  const matches = (opts || []).filter(o => !q || o.display.toLowerCase().includes(q)).slice(0, 8);
  if (!episodeId || opts === null) {
    return <input style={input} disabled={locked} value={val || ''} onChange={e => onPick(e.target.value)} />;
  }
  return (
    <div style={{ position: 'relative' }}>
      <input style={input} disabled={locked} value={val || ''} placeholder="Start typing a staff name…"
        onChange={e => { onPick(e.target.value); setOpenList(true); }}
        onFocus={() => setOpenList(true)}
        onBlur={() => setTimeout(() => setOpenList(false), 180)} />
      {openList && !locked && matches.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40,
          background: '#fff', border: '1px solid ' + T.border, borderRadius: 8,
          boxShadow: '0 8px 22px rgba(15,42,69,0.14)', maxHeight: 220, overflowY: 'auto', marginTop: 3 }}>
          {matches.map(o => (
            <div key={o.id}
              onMouseDown={e => { e.preventDefault(); onPick(o.display); setOpenList(false); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: T.ink,
                borderBottom: '1px solid ' + T.bg }}>
              {o.display}
              <span style={{ color: T.inkSoft, fontSize: 11.5, marginLeft: 6 }}>{o.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FormField({ f, content, locked, setField, facilityName, rates, identity, episodeId }) {
  if (!fieldVisible(f, content)) return null;
  const val = content[f.key];
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontWeight:600, fontSize:13, marginBottom:3 }}>{f.label}</div>
      {f.prompt && <div style={{ fontSize:12, color:T.inkSoft, marginBottom:5, lineHeight:1.45, whiteSpace:'pre-wrap' }}>{resolveTokens(f.prompt, facilityName, rates, identity)}</div>}
      {f.type === 'display' &&
        <div style={{ fontSize:13.5, color:T.ink, lineHeight:1.55, background:T.bg,
          borderRadius:8, padding:'11px 14px', whiteSpace:'pre-wrap' }}>{resolveTokens(f.text, facilityName, rates, identity)}</div>}
      {f.type === 'narrative' && !f.default_now &&
        <textarea style={{ ...input, minHeight:72, resize:'vertical' }} disabled={locked}
          value={val || ''} onChange={e => setField(f.key, e.target.value)} />}
      {f.type === 'narrative' && f.default_now &&
        <NarrativeWithNow f={f} val={val} locked={locked} setField={setField} />}
      {f.type === 'rating_scale' && !Array.isArray(f.auto_sum) &&
        <RatingRow min={f.min ?? 0} max={f.max ?? 10} value={val ?? ''} disabled={locked}
          onChange={v => setField(f.key, v)} />}
      {f.type === 'rating_scale' && Array.isArray(f.auto_sum) &&
        <AutoSumField f={f} content={content} setField={setField} />}
      {f.type === 'multi_choice' &&
        <ChoiceChips options={f.options || []} value={val} multi disabled={locked}
          onChange={v => setField(f.key, v)} />}
      {f.type === 'single_choice' &&
        <ChoiceChips options={f.options || []} value={val || ''} disabled={locked}
          onChange={v => setField(f.key, v)} />}
      {f.type === 'yesno' && <>
        <YesNo value={val || ''} disabled={locked} sev={f.severity_color}
          onChange={v => setField(f.key, v)} />
        {f.comments && (val === 'yes' || content[f.key + '_comments']) &&
          <textarea style={{ ...input, minHeight:44, marginTop:7 }} disabled={locked}
            placeholder="Comments" value={content[f.key + '_comments'] || ''}
            onChange={e => setField(f.key + '_comments', e.target.value)} />}
      </>}
      {f.type === 'time_range' &&
        <TimeRange value={val} disabled={locked} onChange={v => setField(f.key, v)} />}
      {f.type === 'date' &&
        <input type="date" style={{ ...input, width:'auto' }} disabled={locked}
          value={val || ''} onChange={e => setField(f.key, e.target.value)} />}
      {f.type === 'time' &&
        <input type="time" style={{ ...input, width:'auto' }} disabled={locked}
          value={val || ''} onChange={e => setField(f.key, e.target.value)} />}
      {f.type === 'datetime' &&
        <DateTimeField f={f} val={val} locked={locked} setField={setField} />}
      {f.type === 'staff_lookup' &&
        <StaffTypeahead val={val} locked={locked} episodeId={episodeId}
          onPick={v => setField(f.key, v)} />}
      {f.type === 'med_disposition' &&
        <MedDispositionTable val={val} locked={locked} episodeId={episodeId}
          onChange={v => setField(f.key, v)} />}
      {!['display','narrative','rating_scale','multi_choice','single_choice','yesno','time_range','date','datetime','time','staff_lookup','med_disposition'].includes(f.type) &&
        <input style={input} disabled={locked} value={val || ''}
          onChange={e => setField(f.key, e.target.value)} />}
    </div>
  );
}

export default function EMRNotes({ patientId, episodeId, categories, formKeys, heading, packetKey, facilityName, rates, identity , canCosign }) {

  const catOf = useCallback((n, formsList) => {
    const f = (formsList || []).find(x => x.form_key === n.form_key);
    return (f && f.category) || 'clinical';
  }, []);
  const [notes, setNotes] = useState([]);
  const [pktStatus, setPktStatus] = React.useState(null);
  React.useEffect(() => {
    if (!packetKey || !patientId) return;
    axios.get(`${API}/api/emr/packet/${packetKey}/status?patient_id=${patientId}&scope_episode=${episodeId || ''}`)
      .then(r => setPktStatus(r.data)).catch(() => setPktStatus(null));
  }, [packetKey, patientId, notes]);
  const effKeys = packetKey ? (pktStatus ? pktStatus.docs.map(d => d.form_key) : []) : formKeys;
  const effFacilityName = facilityName ||
    ((notes.find(n => n.ep_is_current && n.ep_facility_name) || notes.find(n => n.ep_facility_name) || {}).ep_facility_name) || '';

  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);   // {id, note_type, form_key, formDef, content, status}
  const [showVoided, setShowVoided] = useState(false);
  const [gate, setGate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [signOpen, setSignOpen] = useState(false);
  const resetSigState = () => { setClientSigDone(false); setClientSigOpen(false); setClientSigImg(null); setClientSigErr(''); setNoteSigs([]); };
  const [pin, setPin] = useState('');
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSig, setEnrollSig] = useState(null);
  const [enrollPin, setEnrollPin] = useState('');
  const [enrollPin2, setEnrollPin2] = useState('');
  const [enrollErr, setEnrollErr] = useState('');
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [sigEnrolled, setSigEnrolled] = useState(null); // null = unknown yet
  const [clientSigOpen, setClientSigOpen] = useState(false);
  const [advancing, setAdvancing] = useState(null); // {title, n, total} | 'complete'
  // COWS / CIWA-Ar banding on sign (Catherine 43006a7f): the facility's response text + witness stamps.
  const [clinBanner, setClinBanner] = useState(null);
  const [clinStamps, setClinStamps] = useState({});
  const clinStamp = async (kind) => {
    if (!clinBanner || !clinBanner.alert_id) return;
    try { await axios.post(`${API}/api/emr/clinical-alerts/${clinBanner.alert_id}/stamp`, { kind }); } catch { /* 409 = already stamped */ }
    setClinStamps(p => ({ ...p, [kind]: true }));
  };
  const [clientSigImg, setClientSigImg] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientSigDone, setClientSigDone] = useState(false);
  const [noteSigs, setNoteSigs] = useState([]);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidErr, setVoidErr] = useState('');
  const canVoid = (() => {
    try {
      const tk = localStorage.getItem('tcio_token') || '';
      const pl = JSON.parse(atob(tk.split('.')[1] || '') || '{}');
      return ['admin', 'ceo'].includes(pl.role);
    } catch { return false; }
  })();
  const canReopen = (() => {
    try {
      const tk = localStorage.getItem('tcio_token') || '';
      const pl = JSON.parse(atob(tk.split('.')[1] || '') || '{}');
      return ['admin', 'ceo', 'program_manager', 'clinical_director'].includes(pl.role);
    } catch { return false; }
  })() || !!canCosign;
  const reopenNote = async () => {
    if (!editing || !editing.id) return;
    const reason = window.prompt('What needs to be corrected? The author is notified, the note reopens for them, and they sign it again. The original signed version is kept.');
    if (reason === null) return;
    if (reason.trim().length < 10) { setErr('Write what needs to be corrected (at least 10 characters)'); return; }
    try {
      await axios.post(`${API}/api/emr/notes/${editing.id}/reopen`, { reason: reason.trim() });
      setEditing({ ...editing, status: 'kicked_back' });
      window.alert('Sent back to the author for correction.');
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Could not send the note back'); }
  };
  const doVoid = async () => {
    setVoidErr('');
    if (voidReason.trim().length < 10) { setVoidErr('Write the reason (at least 10 characters)'); return; }
    setVoidBusy(true);
    try {
      await axios.post(`${API}/api/emr/notes/${editing.id}/void`, { reason: voidReason.trim() });
      setVoidOpen(false); setVoidReason('');
      setEditing(null); setGate(null);
      await load();
    } catch (e2) { setVoidErr((e2.response && e2.response.data && e2.response.data.error) || 'Void failed'); }
    setVoidBusy(false);
  };
  const [clientSigBusy, setClientSigBusy] = useState(false);
  const [clientSigErr, setClientSigErr] = useState('');
  const saveClientSig = async () => {
    setClientSigErr('');
    if (!clientSigImg) { setClientSigErr('Client must sign first'); return; }
    if (!clientName.trim()) { setClientSigErr('Enter the client name'); return; }
    setClientSigBusy(true);
    try {
      await axios.post(`${API}/api/emr/notes/${editing.id}/client-signature`,
        { image_base64: clientSigImg, client_name: clientName.trim() });
      setClientSigDone(true); setClientSigOpen(false); setClientSigImg(null);
      // Client-sign flow: roll straight into the next form still missing client ink.
      if (packetKey) {
        try {
          const qs = (await axios.get(`${API}/api/emr/patients/${patientId}/client-pass-queue`,
            { params: { packet_keys: packetKey, ...(episodeId ? { episode_id: episodeId } : {}) } })).data;
          const nx = qs.docs.find(d => !d.client_signed);
          if (nx) {
            setAdvancing({ title: nx.title, n: qs.signed + 1, total: qs.total });
            setTimeout(() => {
              setAdvancing(null);
              if (nx.note_id) openExisting(nx.note_id); else openNewForm(nx.form_key);
            }, 1600);
          } else {
            setAdvancing('complete');
            setTimeout(() => { setAdvancing(null); setEditing(null); }, 1800);
          }
        } catch { /* advance is best-effort */ }
      }
    } catch (e2) { setClientSigErr((e2.response && e2.response.data && e2.response.data.error) || 'Failed to save client signature'); }
    setClientSigBusy(false);
  };
  const checkEnrollment = async () => {
    try {
      const r = await axios.get(`${API}/api/emr/signature/status`);
      setSigEnrolled(!!r.data.enrolled);
      return !!r.data.enrolled;
    } catch { setSigEnrolled(false); return false; }
  };
  const enroll = async () => {
    setEnrollErr('');
    if (!enrollSig) { setEnrollErr('Draw your signature first'); return; }
    if (!/^\d{4}$/.test(enrollPin)) { setEnrollErr('PIN must be exactly 4 digits'); return; }
    if (enrollPin !== enrollPin2) { setEnrollErr('PINs do not match'); return; }
    setEnrollBusy(true);
    try {
      await axios.post(`${API}/api/emr/signature/enroll`, { image_base64: enrollSig, pin: enrollPin });
      setSigEnrolled(true); setEnrollOpen(false);
      setEnrollSig(null); setEnrollPin(''); setEnrollPin2('');
      setSignOpen(true);
    } catch (e2) { setEnrollErr((e2.response && e2.response.data && e2.response.data.error) || 'Enrollment failed'); }
    setEnrollBusy(false);
  };
  const openSign = async () => {
    const ok = sigEnrolled === null ? await checkEnrollment() : sigEnrolled;
    if (ok) setSignOpen(true); else setEnrollOpen(true);
  };
  const [attest, setAttest] = useState('I attest that this documentation is accurate and complete.');
  const [savedAt, setSavedAt] = useState(null);
  const [dictBusy, setDictBusy] = useState(false);
  const [dictResult, setDictResult] = useState(null);
  const [chartBusy, setChartBusy] = useState(false);
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);
  const [chartResult, setChartResult] = useState(null);

  const load = useCallback(async () => {
    try { setNotes((await axios.get(`${API}/api/emr/notes`,
      { params: { patient_id: patientId, ...(episodeId ? { scope_episode: episodeId } : {}) } })).data); setErr(''); }
    catch { setErr('Could not load notes'); }
  }, [patientId, episodeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => {
    try { setForms((await axios.get(`${API}/api/emr/forms`)).data); } catch {}
  })(); }, []);

  const openNewForm = async (formKey) => {
    resetSigState();
    setGate(null); setSignOpen(false); setReview(null); setRevise(null); setBusy(true);
    try {
      const fd = (await axios.get(`${API}/api/emr/forms/${formKey}`)).data;
      // Pull-forward: eligible forms with a prior finalized copy offer pre-population
      // (readmits/step-downs). Confirmed -> create NOW with server-side merge, open populated.
      try {
        const pf = (await axios.get(`${API}/api/emr/patients/${patientId}/pull-forward-check`,
          { params: { form_key: formKey } })).data;
        if (pf.available && window.confirm(
          `Pull forward from the ${fd.title || 'assessment'} finalized ${pf.source_date} by ${pf.source_author}?\n\nOK = pre-populate (review every section) · Cancel = start blank`)) {
          const r = await axios.post(`${API}/api/emr/notes`, {
            patient_id: patientId, episode_id: episodeId, note_type: fd.note_type,
            form_key: formKey, pull_forward: true, content: {} });
          setEditing({ id: r.data.id, note_type: fd.note_type, form_key: formKey, formDef: fd,
            status: r.data.status, content: r.data.content || {} });
          setBusy(false); return;
        }
      } catch { /* pull-forward is best-effort; blank path always works */ }
      let pre = {};
      try { pre = (await axios.get(`${API}/api/emr/prefill`,
        { params: { form_key: formKey, episode_id: episodeId } })).data || {}; } catch {}
      setEditing({ id:null, note_type: fd.note_type, form_key: formKey, formDef: fd,
        status:'new', content: pre });
    } catch { setErr('Could not load form'); }
    setBusy(false);
  };

  // REVOKE A SIGNED ROI (Chad 11/3): the original is never deleted. This creates a Revocation of Consent with the ROI's
  // recipient filled in, stamps the original as revoked, and opens the revocation for the client to sign.
  const revokeRoi = async () => {
    if (!editing || !editing.id) return;
    const reason = window.prompt('Reason for revoking this consent (optional):', '') ; if (reason === null) return;
    if (!window.confirm('Revoke this consent to release information? The original stays in the chart marked REVOKED and the client signs a revocation.')) return;
    try {
      const c = editing.content || {};
      const r = await axios.post(`${API}/api/emr/notes`, { patient_id: patientId, episode_id: editing.episode_id || episodeId, note_type: 'progress_note', form_key: 'roi_revocation',
        content: { recipient_name: c.recipient_name || '', recipient_org: c.recipient_org || '', original_signed: editing.created_at ? new Date(editing.created_at).toLocaleDateString('en-US') : '',
          reason: reason || '', revocation_date: new Date().toISOString().slice(0, 10), original_note_id: editing.id } });
      await axios.post(`${API}/api/emr/notes/${editing.id}/revoke`, { revocation_note_id: r.data.id, reason });
      await load(); await openExisting(r.data.id);
    } catch (e) { alert((e.response && e.response.data && e.response.data.error) || e.message); }
  };
  const openExisting = async (id) => {
    setGate(null); setSignOpen(false); setReview(null); setRevise(null); setBusy(true);
    resetSigState();
    try {
      const d = (await axios.get(`${API}/api/emr/notes/${id}`)).data;
      setNoteSigs(d.signatures || []);
      if ((d.signatures || []).some(s => s.signature_role === 'client')) setClientSigDone(true);
      const cur = d.versions.find(v => v.id === d.note.current_version_id) || d.versions[0];
      let formDef = null;
      if (d.note.form_key) {
        try { formDef = (await axios.get(`${API}/api/emr/forms/${d.note.form_key}`)).data; } catch {}
      }
      setEditing({ id: d.note.id, note_type: d.note.note_type, form_key: d.note.form_key,
        formDef, status: d.note.status, content: (cur && cur.content) || {} });
      setAddenda((d.versions || []).filter(v => v.source === 'addendum')
        .map(v => ({ id: v.id, text: v.narrative_text, by: v.edited_by_name, at: v.created_at }))
        .sort((a, b) => new Date(a.at) - new Date(b.at)));
      setAddOpen(false); setAddText('');
      const fails = d.rule_results.filter(r => !r.passed && r.severity === 'blocking' && !r.resolved_at);
      if (d.note.status === 'kicked_back' && fails.length)
        setGate({ status:'kicked_back', results:d.rule_results, summary:{ blocking_failed:fails.length } });
    } catch { setErr('Could not open note'); }
    setBusy(false);
  };

  const setField = (f, v) => setEditing(e => ({ ...e, content: { ...e.content, [f]: v } }));

  const [review, setReview] = useState(null);
  const [revise, setRevise] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addenda, setAddenda] = useState([]);
  const [reviseBusy, setReviseBusy] = useState(false);
  const aiRevise = async () => {
    if (!editing || !editing.id || !review) return;
    setReviseBusy(true); setErr(''); setRevise(null);
    try {
      const r = await axios.post(`${API}/api/emr/notes/${editing.id}/ai-revise`, { findings: review.findings || [] });
      if (r.data.error) setErr(r.data.error); else setRevise(r.data);
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'AI revise failed'); }
    setReviseBusy(false);
  };
  const applyChange = (ch) => {
    setField(ch.key, ch.proposed);
    setRevise(rv => rv ? { ...rv, changes: rv.changes.filter(c => c.key !== ch.key) } : rv);
  };
  const applyAll = () => {
    if (!revise) return;
    for (const ch of revise.changes) setField(ch.key, ch.proposed);
    setRevise(rv => rv ? { ...rv, changes: [] } : rv);
  };
  const [reviewBusy, setReviewBusy] = useState(false);
  const aiReview = async () => {
    if (!editing || !editing.id) { setErr('Save the draft once before running AI Review'); return; }
    setReviewBusy(true); setErr(''); setReview(null);
    try {
      await saveDraft();  // capture latest typing; review reads the saved version
      const r = await axios.post(`${API}/api/emr/notes/${editing.id}/ai-review`);
      setReview(r.data);
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'AI review failed'); }
    setReviewBusy(false);
  };

  const saveDraft = async () => {
    if (!editing) return null;
    setBusy(true); setErr('');
    try {
      let id = editing.id;
      if (!id) {
        const r = await axios.post(`${API}/api/emr/notes`, {
          patient_id: patientId, episode_id: episodeId, note_type: editing.note_type,
          form_key: editing.form_key || null, content: editing.content });
        id = r.data.id;
        setEditing(e => ({ ...e, id, status: r.data.status,
          content: r.data.content ? r.data.content : e.content }));
      } else {
        const r = await axios.post(`${API}/api/emr/notes/${id}/versions`,
          { content: editing.content, source:'clinician_edit' });
        setEditing(e => ({ ...e, status: r.data.status }));
      }
      setSavedAt(new Date());
      await load(); setBusy(false); return id;
    } catch (e2) {
      setErr((e2.response && e2.response.data && e2.response.data.error) || 'Save failed');
      setBusy(false); return null;
    }
  };

  const runDictation = async (text) => {
    if (!editing || !text.trim()) return;
    setDictBusy(true); setErr(''); setDictResult(null);
    try {
      // Save FIRST: persists any unsaved typing so COALESCE protects it from the AI.
      const id = await saveDraft();
      if (!id) { setDictBusy(false); return; }
      const r = await axios.post(`${API}/api/emr/notes/${id}/ai-draft`, { transcript: text });
      setEditing(e => ({ ...e, id, content: r.data.content }));
      setDictResult(r.data);
      await load();
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'AI draft failed'); }
    setDictBusy(false);
  };

  const runVerbatim = async (text) => {
    if (!editing || !text.trim()) return;
    setPasteBusy(true); setErr(''); setPasteResult(null);
    try {
      // Save FIRST: persists any unsaved typing so COALESCE protects it from the AI.
      const id = await saveDraft();
      if (!id) { setPasteBusy(false); return; }
      const r = await axios.post(`${API}/api/emr/notes/${id}/ai-draft`,
        { transcript: text, mode: 'verbatim', capture_type: 'document_upload' });
      setEditing(e => ({ ...e, id, content: r.data.content }));
      setPasteResult(r.data);
      await load();
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Verbatim insert failed'); }
    setPasteBusy(false);
  };

  const runChartDraft = async () => {
    if (!editing) return;
    setChartBusy(true); setErr(''); setChartResult(null);
    try {
      // Save FIRST: persists any unsaved typing so COALESCE protects it from the AI.
      const id = await saveDraft();
      if (!id) { setChartBusy(false); return; }
      const r = await axios.post(`${API}/api/emr/notes/${id}/chart-draft`, {});
      setEditing(e => ({ ...e, id, content: r.data.content }));
      setChartResult(r.data);
      await load();
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Chart draft failed'); }
    setChartBusy(false);
  };

  const submit = async () => {
    const id = await saveDraft();
    if (!id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/emr/notes/${id}/submit`);
      setGate(r.data);
      setEditing(e => ({ ...e, status: r.data.status }));
      await load();
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Submit failed'); }
    setBusy(false);
  };

  const sign = async () => {
    if (!editing || !editing.id) return;
    setBusy(true); setErr('');
    try {
      // CO-SIGN SAVES FIRST (ticket 9746fd69): the supervisor's edits are sent before the signature. If they cannot be saved, nothing is signed.
      if (editing.status === 'pending_cosign') {
        try { await axios.post(`${API}/api/emr/notes/${editing.id}/versions`, { content: editing.content, source: 'cosigner_edit' }); }
        catch (se) {
          setErr('Your changes could not be saved, so this note was NOT signed: ' + ((se.response && se.response.data && se.response.data.error) || 'save failed') + ' Try again, and report it if it keeps happening.');
          setBusy(false); return;
        }
      }
      const r = await axios.post(`${API}/api/emr/notes/${editing.id}/sign`,
        sigEnrolled ? { method:'pin', pin, attestation_text: attest }
                    : { method:'attestation', attestation_text: attest });
      setPin('');
      setEditing(e => ({ ...e, status: r.data.status }));
      setSignOpen(false); setGate(null);
      if (r.data.clinical) { setClinStamps({}); setClinBanner(r.data.clinical); }
      await load();
      // Intake-room flow: signing a packet doc rolls straight into the next incomplete one.
      if (packetKey && ['finalized', 'pending_cosign'].includes(r.data.status)) {
        try {
          const ps = (await axios.get(`${API}/api/emr/packet/${packetKey}/status?patient_id=${patientId}&scope_episode=${episodeId || ''}`)).data;
          setPktStatus(ps);
          if (ps.next_incomplete) {
            setAdvancing({ title: ps.next_incomplete.title, n: ps.complete + 1, total: ps.total });
            const nx = ps.next_incomplete;
            setTimeout(() => {
              setAdvancing(null);
              if (nx.note_id) openExisting(nx.note_id); else openNewForm(nx.form_key);
            }, 1600);
          } else {
            setAdvancing('complete');
            setTimeout(() => { setAdvancing(null); setEditing(null); }, 1800);
          }
        } catch { /* advance is best-effort; the list still shows Continue */ }
      }
    } catch (e2) {
      const d = e2.response && e2.response.data;
      if (d && d.enroll_required) { setSignOpen(false); setEnrollOpen(true); setErr(''); }
      else setErr((d && d.error) || 'Sign failed');
    }
    setBusy(false);
  };

  let mirror = [];
  try {
    mirror = editing
      ? (editing.formDef ? mirrorFormRules(editing.formDef, editing.content)
                         : mirrorLegacy(editing.note_type, editing.content))
      : [];
  } catch (mErr) { console.error('mirror failed:', mErr); mirror = []; }
  const mBlock = mirror.filter(r => !r.passed && r.severity === 'blocking').length;
  const mWarn = mirror.filter(r => !r.passed && r.severity === 'warning').length;
  const mPass = mirror.filter(r => r.passed).length;
  const locked = editing && (['finalized','amended','voided'].includes(editing.status) || (editing.status === 'pending_cosign' && !canCosign));
  const canSign = editing && editing.status === 'pending_compliance_gate';

  // group fields into sections
  let sections = [];
  if (editing && editing.formDef) {
    const map = new Map();
    for (const f of (editing.formDef.fields || [])) {
      const g = f.group || '';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(f);
    }
    sections = [...map.entries()];
  }

  return (
    <div style={{ ...card, marginTop:16 }}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
        display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <strong>{heading || 'Clinical Notes'}</strong>
        <select style={{ ...input, width:'auto' }} value="" disabled={!episodeId}
          onChange={e => e.target.value && openNewForm(e.target.value)}>
          <option value="">+ New note…</option>
          {['intake','clinical', 'treatment_plan','medical','nursing','intake_packet','discharge_planning']
            .filter(c => !categories || categories.includes(c))
            .filter(c => forms.some(f => (f.category || 'clinical') === c && (!effKeys || effKeys.includes(f.form_key)))).map(c => (
            <optgroup key={c} label={(c === 'intake_packet' || c === 'discharge_planning') && heading ? heading : { intake:'Intake', clinical:'Clinical', medical:'Medical', nursing:'Nursing', intake_packet:'Intake Packet', discharge_planning:'Discharge' }[c]}>
              {forms.filter(f => (f.category || 'clinical') === c && (!effKeys || effKeys.includes(f.form_key))).sort((a, b) => effKeys ? effKeys.indexOf(a.form_key) - effKeys.indexOf(b.form_key) : 0).map(f =>
                <option key={f.form_key} value={f.form_key}>{f.title}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {!editing && packetKey && pktStatus && (
        <div style={{ margin:'12px 18px 0', padding:'13px 16px', background:T.surface,
          border:`1px solid ${T.b200}`, borderRadius:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontWeight:600, fontSize:13.5 }}>
              {pktStatus.complete} of {pktStatus.total} documents complete
              {pktStatus.instance && pktStatus.instance.status === 'complete' &&
                <span style={{ marginLeft:8, background:T.passBg, color:T.pass, borderRadius:99,
                  padding:'1px 9px', fontSize:11, fontWeight:600 }}>PACKET COMPLETE</span>}
              {pktStatus.instance && pktStatus.instance.status === 'in_progress' &&
                <span style={{ marginLeft:8, background:T.blockingBg, color:T.blocking, borderRadius:99,
                  padding:'1px 9px', fontSize:11, fontWeight:600 }}>IN PROGRESS</span>}
            </div>
            {pktStatus.instance && pktStatus.next_incomplete && (
              <button style={{ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600, fontSize:13.5,
                cursor:'pointer', border:'1px solid ' + T.rose, background:T.rose, color:'#fff' }}
                onClick={() => pktStatus.next_incomplete.note_id
                  ? openExisting(pktStatus.next_incomplete.note_id)
                  : openNewForm(pktStatus.next_incomplete.form_key)}>
                Continue → {pktStatus.next_incomplete.title}
              </button>
            )}
            {!pktStatus.instance && pktStatus.next_incomplete && (
              <button style={{ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600, fontSize:13,
                cursor:'pointer', border:'1px solid ' + T.b200, background:T.surface, color:T.ink }}
                onClick={() => axios.post(`${API}/api/emr/packet/${packetKey}/start`, { patient_id: patientId, episode_id: episodeId })
                  .then(() => axios.get(`${API}/api/emr/packet/${packetKey}/status?patient_id=${patientId}&scope_episode=${episodeId || ''}`))
                  .then(r => setPktStatus(r.data)).catch(() => {})}>
                Start {heading || 'Packet'}
              </button>
            )}
          </div>
          <div style={{ marginTop:10, height:7, background:T.b100, borderRadius:99, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pktStatus.total ? Math.round(100 * pktStatus.complete / pktStatus.total) : 0}%`,
              background:T.rose, borderRadius:99, transition:'width .3s' }} />
          </div>
          <div style={{ marginTop:9, display:'flex', gap:6, flexWrap:'wrap' }}>
            {pktStatus.sections.map(s => (
              <span key={s.section_no} style={{ fontSize:11.5, fontWeight:600, borderRadius:99, padding:'2px 10px',
                background: s.complete === s.total ? T.passBg : T.b100,
                color: s.complete === s.total ? T.pass : T.inkSoft }}>
                {s.section_no}. {s.section_name} {s.complete}/{s.total}
              </span>
            ))}
          </div>
        </div>
      )}

      {err && <div style={{ margin:'12px 18px 0', padding:'9px 13px', background:T.blockingBg,
        color:T.blocking, borderRadius:8, fontSize:13, fontWeight:600 }}>{err}</div>}

      {!editing && (
        <div style={{ padding:18 }}>
          {notes.length === 0 && <div style={{ color:T.inkSoft }}>No notes yet.</div>}
          {(() => {
            const inScope = notes.filter(n => effKeys ? effKeys.includes(n.form_key) : (!categories || categories.includes(catOf(n, forms))));
            const voidedCount = inScope.filter(n => n.status === 'voided').length;
            const shown = showVoided ? inScope : inScope.filter(n => n.status !== 'voided');
            const row = (n, dim) => (
              <div key={n.id} onClick={() => openExisting(n.id)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', opacity: n.status === 'voided' ? 0.55 : (dim ? 0.75 : 1),
                  padding:'10px 12px', borderBottom:`1px solid ${T.b100}`, cursor:'pointer' }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13.5 }}>{n.form_key === 'care_team_communication' ? 'Care Team Communication' : (n.form_title || NT_LABEL[n.note_type] || n.note_type)}</div>
                  <div style={{ fontSize:12, color:T.inkSoft }}>
                    {n.author_name || '—'} · {new Date(n.created_at).toLocaleString()} · v{n.version_count}
                    {Number(n.open_blocking) > 0 && <span style={{ color:T.blocking, fontWeight:600 }}> · {n.open_blocking} blocking</span>}
                  </div>
                </div>
                <span style={{ display:'flex', alignItems:'center', gap:8 }}>{n.revoked_at && <span style={{ fontSize:11, fontWeight:700, color:'#b91c1c', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:6, padding:'2px 7px' }}>REVOKED {new Date(n.revoked_at).toLocaleDateString('en-US')}</span>}<StatusPill status={n.status} /></span>
              </div>
            );
            const current = shown.filter(n => n.ep_is_current !== false);
            const prior = shown.filter(n => n.ep_is_current === false);
            const groups = [];
            prior.forEach(n => {
              let g = groups.find(x => x.key === (n.episode_id || 'none'));
              if (!g) { g = { key: n.episode_id || 'none', n0: n, items: [] }; groups.push(g); }
              g.items.push(n);
            });
            const d8 = (d) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month:'numeric', day:'numeric', year:'2-digit' }) : '?';
            let isAdmin = false;
            try {
              const tk = localStorage.getItem('tcio_token') || '';
              const pl = JSON.parse(atob(tk.split('.')[1] || '') || '{}');
              isAdmin = ['admin', 'ceo'].includes(pl.role);
            } catch {}
            return (
              <>
                {current.map(n => row(n, false))}
                {groups.map(g => (
                  <div key={g.key}>
                    <div style={{ margin:'16px -12px 4px', padding:'7px 12px', background:'#f2f2f2',
                      borderRadius:8, fontSize:11.5, fontWeight:600, letterSpacing:0.4, color:T.inkSoft }}>
                      RECORD FROM {(g.n0.ep_facility_name || 'UNKNOWN FACILITY').toUpperCase()} — {g.n0.ep_loc || '?'} · {d8(g.n0.ep_admit)} – {d8(g.n0.ep_discharge)} · closed episode
                    </div>
                    {g.items.map(n => row(n, true))}
                  </div>
                ))}
                {isAdmin && voidedCount > 0 && (
                  <div onClick={() => setShowVoided(v => !v)}
                    style={{ padding:'8px 12px 2px', fontSize:11, color:'#c5c9ce', cursor:'pointer', textAlign:'right' }}>
                    {showVoided ? 'hide voided' : voidedCount + ' voided'}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {editing && (<EditorErrorBoundary>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 250px', gap:0 }}>
          <div style={{ padding:18, borderRight:`1px solid ${T.b100}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                <strong style={{ fontSize:15 }}>
                  {editing.formDef ? editing.formDef.title : (NT_LABEL[editing.note_type] || editing.note_type)}
                </strong>
                <StatusPill status={editing.status === 'new' ? 'in_review' : editing.status} />
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {editing.id &&
                  <button style={{ background:'#fff', color:T.b700, border:'1.5px solid '+T.b300,
                    borderRadius:8, padding:'7px 13px', fontWeight:600, fontSize:12.5, cursor:'pointer' }}
                    onClick={async () => {
                      try {
                        const tk = localStorage.getItem('tcio_token') || '';
                        const r = await fetch(`${API}/api/emr/notes/${editing.id}/pdf`, { headers:{ Authorization:'Bearer '+tk } });
                        if (!r.ok) { alert('Could not generate the PDF (' + r.status + ')'); return; }
                        const blob = await r.blob();
                        const cd = r.headers.get('Content-Disposition') || '';
                        const m = cd.match(/filename="([^"]+)"/);
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = (m && m[1]) || 'note.pdf';
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
                      } catch (e) { alert('Download failed: ' + e.message); }
                    }}>Download PDF</button>}
                {(() => { const cur = (notes || []).find(x => x.id === editing.id) || {}; return (<>
                  {editing.id && editing.form_key === 'roi_release_confidential_info' && cur.client_signed && !cur.revoked_at &&
                    <button style={{ ...btn(false), marginLeft:8, color:'#b91c1c', borderColor:'#fecaca' }} onClick={revokeRoi}>Revoke consent</button>}
                  {cur.revoked_at && <span style={{ marginLeft:10, fontSize:12, fontWeight:700, color:'#b91c1c' }}>REVOKED {new Date(cur.revoked_at).toLocaleDateString('en-US')}</span>}
                </>); })()}
                {canVoid && editing.id && editing.status !== 'voided' &&
                  <button style={{ background:'#fff', color:'#b91c1c', border:'1.5px solid #fca5a5',
                    borderRadius:8, padding:'7px 13px', fontWeight:600, fontSize:12.5, cursor:'pointer' }}
                    onClick={() => { setVoidOpen(true); setVoidReason(''); setVoidErr(''); }}>Void</button>}
                <button style={btn(false)} onClick={() => { setEditing(null); setGate(null); setSignOpen(false); setReview(null); setRevise(null); }}>← Notes</button>
              </div>
            </div>
            {voidOpen && (
              <div style={{ background:'#fef2f2', border:'1.5px solid #fca5a5', borderRadius:10,
                padding:'13px 15px', marginBottom:14 }}>
                <div style={{ fontWeight:700, fontSize:13.5, color:'#b91c1c', marginBottom:6 }}>
                  Void this document</div>
                <div style={{ fontSize:12, color:'#7f1d1d', marginBottom:8 }}>
                  The document will be removed from all working views. It is never deleted — the full
                  record, signatures, and your written reason remain in the audit trail.</div>
                <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)}
                  placeholder="Why is this document being voided? (required)"
                  style={{ width:'100%', minHeight:60, border:'1px solid #fca5a5', borderRadius:8,
                    padding:'8px 10px', fontSize:12.5, resize:'vertical', boxSizing:'border-box' }} />
                {voidErr && <div style={{ color:'#dc2626', fontSize:12, fontWeight:600, marginTop:6 }}>{voidErr}</div>}
                <div style={{ display:'flex', gap:10, marginTop:10 }}>
                  <button disabled={voidBusy} onClick={doVoid}
                    style={{ background:'#b91c1c', color:'#fff', border:'none', borderRadius:8,
                      padding:'8px 16px', fontWeight:700, fontSize:12.5, cursor:'pointer' }}>
                    {voidBusy ? 'Voiding…' : 'Confirm Void'}</button>
                  <button style={btn(false)} onClick={() => setVoidOpen(false)}>Cancel</button>
                </div>
              </div>
            )}

            {gate && gate.status === 'kicked_back' && (
              <div style={{ background:T.blockingBg, border:`1px solid ${T.blocking}`, borderRadius:8,
                padding:'11px 14px', marginBottom:14 }}>
                <div style={{ color:T.blocking, fontWeight:600, fontSize:13, marginBottom:5 }}>
                  Kicked back — {gate.summary.blocking_failed} blocking issue{gate.summary.blocking_failed > 1 ? 's' : ''}
                </div>
                {gate.results.filter(r => !r.passed && r.severity === 'blocking').map(r => (
                  <div key={r.rule_code} style={{ fontSize:12.5, color:T.ink }}>• {r.rule_description}</div>
                ))}
              </div>
            )}

            {!locked && editing.formDef && ['concurrent_ur','patient_discharge_summary'].includes(editing.form_key) && (
              <div style={{ marginBottom:14, padding:'12px 14px', background:T.bg, borderRadius:8 }}>
                <button disabled={chartBusy} onClick={runChartDraft}
                  style={{ width:'100%', padding:'11px 0', borderRadius:8, border:'none', cursor:'pointer',
                    fontWeight:600, fontSize:14, color:'#fff', background:T.roseDeep, opacity: chartBusy ? 0.6 : 1 }}>
                  {chartBusy ? 'Reading the chart\u2026' : '\ud83d\udcc4 Draft from chart \u2192 AI writes this UR from signed documents'}
                </button>
                {chartResult && (
                  <div style={{ marginTop:10, fontSize:12 }}>
                    <div style={{ color:T.pass, fontWeight:600 }}>✓ Filled {chartResult.applied.length} field{chartResult.applied.length === 1 ? '' : 's'} from {chartResult.manifest.length} signed document{chartResult.manifest.length === 1 ? '' : 's'}{chartResult.preserved_human.length > 0 ? ` \u00b7 kept your ${chartResult.preserved_human.length}` : ''}</div>
                    {chartResult.risk_fields_requiring_clinician && chartResult.risk_fields_requiring_clinician.length > 0 && (
                      <div style={{ color:T.blocking, fontWeight:600, marginTop:4 }}>
                        Clinician must complete: {chartResult.risk_fields_requiring_clinician.join(', ')}</div>)}
                    {chartResult.dropped_uncited.length > 0 && (
                      <div style={{ color:T.warning, marginTop:4 }}>Removed {chartResult.dropped_uncited.length} uncited claim{chartResult.dropped_uncited.length === 1 ? '' : 's'} from medical-necessity sections</div>)}
                    {chartResult.documentation_gaps.length > 0 && (
                      <div style={{ marginTop:6 }}>
                        <div style={{ color:T.warning, fontWeight:600 }}>Documentation gaps:</div>
                        {chartResult.documentation_gaps.map((g, i) => (
                          <div key={i} style={{ color:T.warning, fontSize:12 }}>⚠ {g}</div>))}
                      </div>)}
                    {chartResult.gaps.length > 0 && (
                      <div style={{ color:T.warning, marginTop:4 }}>Still open: {chartResult.gaps.filter(g => !(chartResult.risk_fields_requiring_clinician || []).includes(g)).join(', ')}</div>)}
                  </div>
                )}
              </div>
            )}
            {!locked && editing.formDef && (
              <div style={{ marginBottom:14, padding:'12px 14px', background:T.bg, borderRadius:8 }}>
                <DictatePanel busy={dictBusy} onSubmit={runDictation}
                  cta="🎙 Dictate → AI drafts this note"
                  submitLabel="Draft this note"
                  placeholder="Tap Record and describe the session \u2014 words appear as you talk. AI fills empty fields only; your entries are never overwritten. Nothing is signed."
                  result={dictResult && (
                    <div style={{ marginTop:10, fontSize:12 }}>
                      <div style={{ color:T.pass, fontWeight:600 }}>✓ Filled {dictResult.applied.length} field{dictResult.applied.length === 1 ? '' : 's'}{dictResult.preserved_human.length > 0 ? ` \u00b7 kept your ${dictResult.preserved_human.length}` : ''}</div>
                      {dictResult.risk_fields_requiring_clinician && dictResult.risk_fields_requiring_clinician.length > 0 && (
                        <div style={{ color:T.blocking, fontWeight:600, marginTop:4 }}>
                          Clinician must complete: {dictResult.risk_fields_requiring_clinician.join(', ')}</div>)}
                      {dictResult.gaps.length > 0 && (
                        <div style={{ color:T.warning, marginTop:4 }}>Still open: {dictResult.gaps.filter(g => !(dictResult.risk_fields_requiring_clinician || []).includes(g)).join(', ')}</div>)}
                    </div>
                  )} />
              </div>
            )}
            {!locked && editing.formDef && (
              <div style={{ marginBottom:14, padding:'12px 14px', background:T.bg, borderRadius:8 }}>
                <PasteVerbatimPanel busy={pasteBusy} onSubmit={runVerbatim}
                  result={pasteResult && (
                    <div style={{ marginTop:10, fontSize:12 }}>
                      <div style={{ color:T.pass, fontWeight:600 }}>✓ Placed {pasteResult.applied.length} field{pasteResult.applied.length === 1 ? '' : 's'} word-for-word{pasteResult.preserved_human.length > 0 ? ` \u00b7 kept your ${pasteResult.preserved_human.length}` : ''}</div>
                      {pasteResult.rejected_not_verbatim && pasteResult.rejected_not_verbatim.length > 0 && (
                        <div style={{ color:T.warning, marginTop:4 }}>
                          {pasteResult.rejected_not_verbatim.length} field{pasteResult.rejected_not_verbatim.length === 1 ? '' : 's'} left blank (no exact match in your paste) — fill by hand: {pasteResult.rejected_not_verbatim.map(x => x.key).join(', ')}</div>)}
                      <div style={{ color:T.inkSoft, marginTop:4 }}>Checkboxes, selections, and risk fields are yours to complete.</div>
                      {pasteResult.gaps.length > 0 && (
                        <div style={{ color:T.warning, marginTop:4 }}>Still open: {pasteResult.gaps.filter(g => !(pasteResult.risk_fields_requiring_clinician || []).includes(g)).join(', ')}</div>)}
                    </div>
                  )} />
              </div>
            )}
            {editing.formDef ? sections.map(([g, fs]) => (
              <div key={g || '_top'}>
                {g && <div style={{ fontSize:16, fontWeight:600,
                  color:T.roseDeep, borderBottom:`1px solid ${T.b200}`, paddingBottom:5,
                  margin:'18px 0 12px' }}>{g}</div>}
                {fs.map(f => <FormField key={f.key} f={f} facilityName={effFacilityName} rates={rates} identity={identity} episodeId={episodeId} content={editing.content}
                  locked={locked} setField={setField} />)}
              </div>
            )) : (
              <div style={{ color:T.inkSoft, fontSize:13, marginBottom:12 }}>
                Legacy note (no form definition).
              </div>
            )}

            {!locked && (
              <div style={{ display:'flex', gap:10, marginTop:6, flexWrap:'wrap' }}>
                <button style={btn(false)} disabled={busy} onClick={saveDraft}>{busy ? 'Saving…' : 'Save Draft'}</button>
                {savedAt && !busy && <span style={{ alignSelf:'center', fontSize:12.5, color:T.pass, fontWeight:600 }}>✓ Saved {savedAt.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}</span>}
                <button style={{ ...btn(false), color:T.info, borderColor:T.info }}
                  disabled={busy || reviewBusy || !editing.id}
                  onClick={aiReview}>{reviewBusy ? 'Reviewing…' : 'AI Review'}</button>
                {!canSign && <button style={btn(true)} disabled={busy} onClick={submit}>Submit to Compliance Gate</button>}
                {canSign && !signOpen && <button style={btn(true)} disabled={busy} onClick={openSign}>Sign Note</button>}
              </div>
            )}
            {review && (
              <div style={{ marginTop:12, border:`1px solid ${T.b200}`, borderRadius:8, padding:'12px 16px' }}>
                {(() => {
                  const V = { strong:{ bg:T.passBg, fg:T.pass, text:'Strong' },
                              acceptable:{ bg:T.warningBg, fg:T.warning, text:'Acceptable' },
                              needs_work:{ bg:T.blockingBg, fg:T.blocking, text:'Needs Work' } };
                  const v = V[review.verdict] || V.acceptable;
                  return <span style={{ background:v.bg, color:v.fg, borderRadius:99, padding:'3px 12px',
                    fontSize:12.5, fontWeight:600 }}>{v.text}</span>;
                })()}
                <span style={{ marginLeft:10, fontSize:12.5, color:T.inkSoft }}>{review.summary}</span>
                {(review.findings || []).map((f, i) => {
                  const S = { critical:{ bg:T.blockingBg, fg:T.blocking }, warning:{ bg:T.warningBg, fg:T.warning }, info:{ bg:T.infoBg, fg:T.info } };
                  const s = S[f.severity] || S.info;
                  return (
                    <div key={i} style={{ background:s.bg, borderRadius:6, padding:'8px 12px', marginTop:8 }}>
                      <span style={{ color:s.fg, fontWeight:600, fontSize:11.5, textTransform:'uppercase' }}>
                        {f.severity} · {(f.area || '').replace('_',' ')}</span>
                      <div style={{ fontSize:12.5, marginTop:2 }}>{f.issue}</div>
                      {f.suggestion && <div style={{ fontSize:12.5, marginTop:4, padding:'6px 10px',
                        background:'#fff', borderLeft:`3px solid ${s.fg}`, borderRadius:4 }}>
                        <b>Suggestion:</b> {f.suggestion}</div>}
                    </div>
                  );
                })}
                <div style={{ display:'flex', gap:10, alignItems:'center', marginTop:10 }}>
                  <button style={{ ...btn(true) }} disabled={reviseBusy || busy}
                    onClick={aiRevise}>{reviseBusy ? 'Drafting fixes…' : 'Fix with AI'}</button>
                  <span style={{ fontSize:11.5, color:T.inkSoft }}>
                    Drafts fixes for documentation findings. Observation findings (MSE selections) stay yours to decide.</span>
                </div>
                <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:8 }}>
                  Advisory coaching — suggestions never auto-apply. You write the note.</div>
              </div>
            )}
            {revise && (
              <div style={{ marginTop:12, border:`1px solid ${T.b200}`, borderRadius:8, padding:'12px 16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <strong style={{ fontSize:13.5 }}>Proposed Fixes ({revise.changes.length})</strong>
                  {revise.changes.length > 1 &&
                    <button style={btn(false)} onClick={applyAll}>Apply All</button>}
                </div>
                {revise.changes.length === 0 &&
                  <div style={{ fontSize:12.5, color:T.inkSoft }}>All proposed fixes applied. Re-run AI Review to verify.</div>}
                {revise.changes.map((ch, i) => (
                  <div key={i} style={{ borderTop:`1px solid ${T.b100}`, paddingTop:10, marginTop:10 }}>
                    <div style={{ ...label, marginBottom:4 }}>{ch.key.replace(/_/g,' ')}</div>
                    <div style={{ fontSize:12, color:T.inkSoft, background:T.b100, borderRadius:6,
                      padding:'7px 10px', textDecoration:'line-through', marginBottom:5 }}>{ch.old}</div>
                    <div style={{ fontSize:12.5, background:T.passBg, borderRadius:6, padding:'7px 10px' }}>{ch.proposed}</div>
                    <button style={{ ...btn(true), marginTop:6, padding:'5px 14px', fontSize:12 }}
                      onClick={() => applyChange(ch)}>Apply</button>
                  </div>
                ))}
                {(revise.notes_for_clinician || []).length > 0 && (
                  <div style={{ marginTop:10, fontSize:12, color:T.inkSoft }}>
                    {revise.notes_for_clinician.map((n, i) => <div key={i}>• {n}</div>)}
                  </div>
                )}
              </div>
            )}
            {editing && editing.content && editing.content._pulled_forward_from && (
        <div style={{ background:'#fef9c3', border:'1px solid #d4b106', color:'#854d0e',
          borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13, fontWeight:600 }}>
          ⟳ Pre-populated from the assessment finalized {editing.content._pulled_forward_from.finalized_date} by {editing.content._pulled_forward_from.author}. Review every section — your signature attests the reviewed content.
        </div>
      )}
      {clinBanner && (() => {
        const b = clinBanner;
        const col = (b.band === 'emergency' || b.band === 'urgent') ? { c: '#dc2626', bg: '#fef2f2' }
                  : b.band === 'notify' ? { c: '#b45309', bg: '#fffbeb' }
                  : b.band === 'watch' ? { c: '#2563eb', bg: '#eff6ff' } : { c: '#15803d', bg: '#f0fdf4' };
        const name = b.scale === 'cows' ? 'COWS' : 'CIWA-Ar';
        const hasAlert = !!b.alert_id;
        return (
          <div onClick={() => setClinBanner(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 480, boxShadow: '0 24px 64px rgba(15,23,42,0.35)' }}>
              <div style={{ fontWeight: 900, fontSize: 16, color: col.c, marginBottom: 4 }}>
                {b.band === 'emergency' ? '\u26A0 CALL 911 \u2014 ' : (b.band === 'urgent' || b.band === 'notify') ? '\u26A0 ' : ''}{name} {b.score != null ? b.score : ''} {'\u2014'} {b.label || 'recorded'}
              </div>
              <div style={{ background: col.bg, border: '1px solid ' + col.c, borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 8 }}>
                <div>{b.response || 'Score recorded.'}</div>
                {b.complicated && b.complicated.length > 0 && <div style={{ marginTop: 6, fontWeight: 700 }}>{'Complicated withdrawal flags: ' + b.complicated.join(', ')}</div>}
                {b.reassess_minutes && <div style={{ marginTop: 6, color: T.inkSoft }}>{'Reassess within ' + (b.reassess_minutes >= 60 ? (b.reassess_minutes / 60) + ' hour' + (b.reassess_minutes > 60 ? 's' : '') : b.reassess_minutes + ' min')}{b.reassess_due_at ? ' \u00B7 due ' + new Date(b.reassess_due_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' }) : ''}</div>}
                {hasAlert && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => clinStamp('provider_notified')} disabled={!!clinStamps.provider_notified}
                      style={{ borderRadius: 8, padding: '7px 12px', font: 'inherit', fontWeight: 600, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: clinStamps.provider_notified ? '#e2e8f0' : '#fff' }}>
                      {clinStamps.provider_notified ? '\u2713 Provider notified' : 'Provider notified'}</button>
                    <button onClick={() => clinStamp('ems_activated')} disabled={!!clinStamps.ems_activated}
                      style={{ borderRadius: 8, padding: '7px 12px', font: 'inherit', fontWeight: 600, fontSize: 13, cursor: 'pointer', border: '1px solid #dc2626', color: '#dc2626', background: clinStamps.ems_activated ? '#e2e8f0' : '#fff' }}>
                      {clinStamps.ems_activated ? '\u2713 911 activated' : '911 activated'}</button>
                  </div>
                )}
              </div>
              {b.ems_text && <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 8 }}>{b.ems_text}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setClinBanner(null)} style={{ borderRadius: 8, padding: '8px 14px', font: 'inherit', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: T.rose, color: '#fff', border: 'none' }}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}
      {advancing && (
              <div style={{ marginTop:12, padding:'14px 16px', background:'#f0fdf4', border:'2px solid #22c55e', borderRadius:10, fontWeight:600, fontSize:14, color:'#15803d' }}>
                {advancing === 'complete'
                  ? '\u2713 Signed \u2014 packet complete!'
                  : `\u2713 Signed \u2014 opening next: ${advancing.title} (${advancing.n} of ${advancing.total})`}
              </div>
            )}
            {editing.form_key === 'authorized_representative' && <PlanRepFormPanel mode="staff" patientId={patientId} episodeId={editing.episode_id || episodeId} />}
            {editing.id && (catOf(editing, forms) === 'intake_packet' || (forms || []).some(x => x.form_key === editing.form_key && x.client_sign)) && (
              <div style={{ marginTop:12, padding:'12px 14px', background:'#fff', border:'1.5px solid #cbd5e1', borderRadius:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#336090' }}>
                    Client Signature {clientSigDone && <span style={{ color:'#22c55e' }}>✓ signed</span>}
                  </div>
                  {!clientSigOpen && !locked &&
                    <button style={btn(false)} onClick={() => setClientSigOpen(true)}>
                      {clientSigDone ? 'Re-sign' : '\u270d Capture Client Signature'}</button>}
                </div>
                {(noteSigs || []).filter(s => s.signature_role === 'client').map((s, i) => (
                  <div key={i} style={{ marginTop:10, display:'flex', alignItems:'center', gap:14 }}>
                    {s.image_url && <img src={s.image_url} alt="client signature"
                      style={{ maxWidth:220, maxHeight:80, border:'1px solid #e2e8f0', borderRadius:6, background:'#fff' }} />}
                    <div style={{ fontSize:12, color:'#475569' }}>
                      <div style={{ fontWeight:600 }}>{s.signer_name}</div>
                      <div>{new Date(s.signed_at).toLocaleString()}</div>
                      {s.witnessed_by && <div style={{ color:'#94a3b8' }}>Witnessed by {s.witnessed_by}</div>}
                    </div>
                  </div>
                ))}
                {clientSigOpen && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ ...label, marginBottom:4 }}>Client name (as it should appear on the document)</div>
                    <input style={{ ...input, maxWidth:320, marginBottom:10 }} value={clientName}
                      onChange={e => setClientName(e.target.value)} placeholder="First Last" />
                    <SignaturePad onChange={setClientSigImg} />
                    {clientSigErr && <div style={{ color:'#dc2626', fontSize:12, fontWeight:600, marginTop:8 }}>{clientSigErr}</div>}
                    <div style={{ display:'flex', gap:10, marginTop:10 }}>
                      <button style={btn(true)} disabled={clientSigBusy} onClick={saveClientSig}>
                        {clientSigBusy ? 'Saving\u2026' : 'Save Client Signature'}</button>
                      <button style={btn(false)} onClick={() => { setClientSigOpen(false); setClientSigImg(null); setClientSigErr(''); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {enrollOpen && (
              <div style={{ marginTop:12, padding:'14px 16px', background:'#fff', border:'2px solid #336090', borderRadius:10 }}>
                <div style={{ fontWeight:600, fontSize:14, color:'#336090', marginBottom:4 }}>Set up your signature</div>
                <div style={{ fontSize:12, color:T.inkSoft, marginBottom:10 }}>
                  One time only: draw your signature and choose a 4-digit PIN. From then on, entering your PIN places this signature on every document you sign.</div>
                <SignaturePad onChange={setEnrollSig} />
                <div style={{ display:'flex', gap:16, marginTop:10, flexWrap:'wrap' }}>
                  <div>
                    <div style={{ ...label, marginBottom:4 }}>Choose PIN</div>
                    <input type="password" inputMode="numeric" maxLength={4} autoComplete="off"
                      value={enrollPin} onChange={e => setEnrollPin(e.target.value.replace(/\D/g, ''))}
                      style={{ ...input, width:120, fontSize:20, letterSpacing:8, textAlign:'center' }} />
                  </div>
                  <div>
                    <div style={{ ...label, marginBottom:4 }}>Confirm PIN</div>
                    <input type="password" inputMode="numeric" maxLength={4} autoComplete="off"
                      value={enrollPin2} onChange={e => setEnrollPin2(e.target.value.replace(/\D/g, ''))}
                      style={{ ...input, width:120, fontSize:20, letterSpacing:8, textAlign:'center' }} />
                  </div>
                </div>
                {enrollErr && <div style={{ color:'#dc2626', fontSize:12, fontWeight:600, marginTop:8 }}>{enrollErr}</div>}
                <div style={{ display:'flex', gap:10, marginTop:12 }}>
                  <button style={btn(true)} disabled={enrollBusy} onClick={enroll}>{enrollBusy ? 'Saving…' : 'Save Signature & PIN'}</button>
                  <button style={btn(false)} onClick={() => setEnrollOpen(false)}>Cancel</button>
                </div>
              </div>
            )}
            {signOpen && canSign && (
              <div style={{ marginTop:12, padding:'12px 14px', background:T.b100, borderRadius:8 }}>
                <div style={{ ...label, marginBottom:4 }}>Attestation</div>
                <textarea style={{ ...input, minHeight:46 }} value={attest} onChange={e => setAttest(e.target.value)} />
                {sigEnrolled && (
                  <div style={{ marginTop:8 }}>
                    <div style={{ ...label, marginBottom:4 }}>Signature PIN</div>
                    <input type="password" inputMode="numeric" maxLength={4} autoComplete="off"
                      value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                      placeholder="4-digit PIN"
                      style={{ ...input, width:140, fontSize:20, letterSpacing:8, textAlign:'center' }} />
                  </div>
                )}
                <div style={{ display:'flex', gap:10, marginTop:8 }}>
                  <button style={btn(true)} disabled={busy || (sigEnrolled && pin.length !== 4)} onClick={sign}>Confirm Signature</button>
                  <button style={btn(false)} onClick={() => setSignOpen(false)}>Cancel</button>
                </div>
              </div>
            )}
            {editing.status === 'finalized' && <div style={{ marginTop:12 }}>
              <div style={{ color:T.pass, fontWeight:600, fontSize:13 }}>
                ✓ Finalized — locked. Future changes require an addendum.</div>
              {addenda.map(a => (
                <div key={a.id} style={{ marginTop:10, padding:'10px 14px', background:T.b100,
                  borderLeft:`3px solid ${T.pass}`, borderRadius:8 }}>
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.6px', color:T.inkSoft, textTransform:'uppercase' }}>
                    Addendum · {a.by} · {new Date(a.at).toLocaleString()}</div>
                  <div style={{ fontSize:13, color:T.ink, whiteSpace:'pre-wrap', marginTop:4 }}>{a.text}</div>
                </div>
              ))}
              {!addOpen && <button style={{ ...btn(false), marginTop:10 }} onClick={() => setAddOpen(true)}>+ Add Addendum</button>}
              {!addOpen && canReopen && <button style={{ ...btn(false), marginTop:10, marginLeft:8 }} onClick={reopenNote}>Send back for correction</button>}
              {addOpen && (
                <div style={{ marginTop:10, padding:'12px 14px', background:T.b100, borderRadius:8 }}>
                  <div style={{ fontSize:12, color:T.inkSoft, marginBottom:6 }}>
                    The addendum is appended below the finalized note with your name and timestamp. The original note never changes.</div>
                  <textarea value={addText} onChange={e => setAddText(e.target.value)} rows={4}
                    placeholder="Addendum text (e.g. correction, late information, clarification)"
                    style={{ ...input, width:'100%', boxSizing:'border-box', resize:'vertical' }} />
                  <div style={{ display:'flex', gap:10, marginTop:8 }}>
                    <button style={btn(true)} disabled={addBusy || !addText.trim()} onClick={async () => {
                      setAddBusy(true);
                      try {
                        const r = await axios.post(`${API}/api/emr/notes/${editing.id}/addendum`, { text: addText.trim() });
                        if (r.data.ok) {
                          setAddenda(a => [...a, { id: r.data.addendum_id, text: addText.trim(), by: 'You', at: r.data.created_at }]);
                          setAddOpen(false); setAddText('');
                        }
                      } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Addendum failed'); }
                      setAddBusy(false);
                    }}>{addBusy ? 'Saving…' : 'Save Addendum'}</button>
                    <button style={btn(false)} onClick={() => { setAddOpen(false); setAddText(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>}
            {editing.status === 'pending_cosign' &&
              <div style={{ marginTop:12 }}>
                <div style={{ color:T.warning, fontWeight:600, fontSize:13, marginBottom:8 }}>
                  {canCosign ? 'Awaiting your co-signature — you may edit the note before signing; your changes are tracked as supervisor edits.' : 'Awaiting supervisor co-signature.'}</div>
                {!signOpen && <button onClick={openSign} disabled={busy}
                  style={{ background:T.warning, color:'#fff', border:'none', borderRadius:8,
                    padding:'8px 16px', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                  Co-sign this note</button>}
                {signOpen && (
                  <div style={{ marginTop:8, padding:'12px 14px', background:T.b100, borderRadius:8 }}>
                    {sigEnrolled && (
                      <input type="password" inputMode="numeric" maxLength={4} autoComplete="off"
                        value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                        placeholder="4-digit PIN"
                        style={{ ...input, width:140, fontSize:20, letterSpacing:8, textAlign:'center', display:'block', marginBottom:8 }} />
                    )}
                    <div style={{ display:'flex', gap:10 }}>
                      <button style={btn(true)} disabled={busy || (sigEnrolled && pin.length !== 4)} onClick={sign}>Confirm Co-sign</button>
                      <button style={btn(false)} onClick={() => { setSignOpen(false); setPin(''); }}>Cancel</button>
                    </div>
                  </div>
                )}
                <div style={{ fontSize:11, color:T.inkSoft, marginTop:6 }}>
                  Server verifies eligibility — the author cannot co-sign their own note.</div>
              </div>}
          </div>

          <div style={{ padding:18, background:T.bg, borderRadius:'0 10px 10px 0' }}>
            <div style={{ ...label, marginBottom:10 }}>Compliance</div>
            <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
              <span style={{ background: mBlock ? T.blockingBg : T.passBg, color: mBlock ? T.blocking : T.pass,
                borderRadius:8, padding:'4px 10px', fontSize:12, fontWeight:600 }}>{mBlock} blocking</span>
              {mWarn > 0 && <span style={{ background:T.warningBg, color:T.warning, borderRadius:8,
                padding:'4px 10px', fontSize:12, fontWeight:600 }}>{mWarn} warning</span>}
              <span style={{ background:T.passBg, color:T.pass, borderRadius:8, padding:'4px 10px',
                fontSize:12, fontWeight:600 }}>{mPass} passed</span>
            </div>
            <div style={{ maxHeight:420, overflowY:'auto' }}>
              {[...mirror].sort((x, y) => (x.passed === y.passed ? 0 : x.passed ? 1 : -1)).map(r => (
                <div key={r.code} style={{ display:'flex', gap:7, alignItems:'baseline', marginBottom:7 }}>
                  <span style={{ color: r.passed ? T.pass : (r.severity === 'warning' ? T.warning : T.blocking),
                    fontWeight:600 }}>{r.passed ? '✓' : '✕'}</span>
                  <span style={{ fontSize:12.5, color: r.passed ? T.inkSoft : T.ink }}>{r.desc}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize:11, color:T.inkSoft, marginTop:12 }}>
              Live preview — the server runs the authoritative check at submission.
            </div>
          </div>
        </div>
      </EditorErrorBoundary>)}
    </div>
  );
}
